import React, { Suspense, lazy, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import HeroSection from './HeroSection';
import AboutSection from './AboutSection';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { applyPageMeta, META_FAQ, META_HOME, META_SERVICES } from './seoMeta';
import { homePageSchemaJsonLd } from './organizationSchema';

const ServicesSection = lazy(() => import('./ServicesSection'));
const PortfolioSection = lazy(() => import('./PortfolioSection'));
const FaqSection = lazy(() => import('./FaqSection'));
const ContactForm = lazy(() => import('./ContactForm'));

function SectionFallback({ label }) {
  return (
    <div
      className="min-h-[20rem] w-full bg-gradient-to-b from-gray-50/80 to-white flex items-center justify-center text-gray-400 text-sm"
      aria-busy="true"
      aria-label={label}
    >
      Loading…
    </div>
  );
}

function HomePage() {
  const location = useLocation();

  useEffect(() => {
    let restoreMeta = () => {};
    let intervalId;

    const startScrollToId = (elementId) => {
      let tries = 0;
      intervalId = window.setInterval(() => {
        tries += 1;
        const el = document.getElementById(elementId);
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
          window.clearInterval(intervalId);
          intervalId = undefined;
        } else if (tries > 50) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }
      }, 100);
    };

    if (location.pathname === '/faq') {
      restoreMeta = applyPageMeta(META_FAQ);
      startScrollToId('faq');
    } else if (location.pathname === '/services') {
      restoreMeta = applyPageMeta(META_SERVICES);
      startScrollToId('services');
    } else {
      restoreMeta = applyPageMeta(META_HOME);
    }

    const SCHEMA_ID = 'webcodecraft-home-organization-schema';
    const prev = document.getElementById(SCHEMA_ID);
    if (prev) prev.remove();
    const ld = document.createElement('script');
    ld.id = SCHEMA_ID;
    ld.type = 'application/ld+json';
    ld.textContent = homePageSchemaJsonLd();
    document.head.appendChild(ld);

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      restoreMeta();
      const script = document.getElementById(SCHEMA_ID);
      if (script) script.remove();
    };
  }, [location.pathname]);

  return (
    <>
      <Header />
      <main id="main-content" className="pt-16 lg:pt-20 min-w-0 overflow-x-clip">
        <HeroSection />
        <Suspense fallback={<SectionFallback label="Loading services section" />}>
          <ServicesSection />
        </Suspense>
        <AboutSection />
        <Suspense fallback={<SectionFallback label="Loading portfolio section" />}>
          <PortfolioSection />
        </Suspense>
        <Suspense fallback={<SectionFallback label="Loading FAQ section" />}>
          <FaqSection />
        </Suspense>
        <Suspense fallback={<SectionFallback label="Loading contact section" />}>
          <ContactForm />
        </Suspense>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default HomePage;
